import { locationPages } from '../../../mocks/location-pages';
import { services } from '../../../mocks/studios';

export default function AllLocationsContent() {
  return (
    <div id="all-locations-content">
      {locationPages.map((location, index) => (
        <section
          key={location.slug}
          className={`py-24 ${index % 2 === 0 ? 'bg-white' : 'bg-stone-50'}`}
        >
          {/* Section label */}
          <div className="max-w-7xl mx-auto px-6 mb-12">
            <div className="flex items-center gap-4">
              <span className="text-amber-600 text-xs font-bold uppercase tracking-widest">
                {String(index + 1).padStart(2, '0')} / {String(locationPages.length).padStart(2, '0')}
              </span>
              <div className="h-px flex-1 bg-stone-200" />
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-6">
            {/* Location header */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start mb-14">
              {/* Info side */}
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-5">
                  <i className="ri-map-pin-2-fill text-amber-500" />
                  {location.city}, {location.state} • {location.studioCount} Studio Locations
                </div>
                <h2 className="font-serif text-4xl md:text-5xl font-bold text-stone-900 mb-4 leading-tight">
                  Studio Rentals in{' '}
                  <em className="text-amber-500 not-italic">{location.city}</em>
                </h2>
                <p className="text-stone-500 text-lg leading-relaxed mb-8">
                  {location.tagline}
                </p>

                {/* Trust signals */}
                <div className="grid grid-cols-2 gap-3 mb-8">
                  {location.localTrustSignals.map((signal) => (
                    <div key={signal} className="flex items-center gap-2.5 bg-white rounded-xl p-3.5 border border-stone-100">
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className="ri-checkbox-circle-fill text-amber-500 text-base" />
                      </div>
                      <span className="text-stone-700 text-xs font-medium leading-snug">{signal}</span>
                    </div>
                  ))}
                </div>

                {/* Neighborhoods */}
                <div className="flex flex-wrap gap-2">
                  {location.neighborhoods.map((n) => (
                    <span
                      key={n}
                      className="flex items-center gap-1.5 bg-stone-100 text-stone-700 text-xs font-medium px-3.5 py-1.5 rounded-full whitespace-nowrap"
                    >
                      <i className="ri-map-pin-2-fill text-amber-500 text-xs" />
                      {n}
                    </span>
                  ))}
                </div>
              </div>

              {/* Hero image side */}
              <div className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <div className="rounded-2xl overflow-hidden h-[480px]">
                  <img
                    src={location.heroImage}
                    alt={`Studio rentals in ${location.city}`}
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div className="absolute -bottom-5 -right-5 bg-stone-900 rounded-2xl px-6 py-4">
                  <p className="text-amber-400 text-xs font-bold uppercase tracking-widest mb-1">Available Now</p>
                  <p className="text-white font-bold text-2xl font-serif">{location.studioCount} Studios</p>
                  <p className="text-stone-400 text-xs">in {location.city}</p>
                </div>
              </div>
            </div>

            {/* Studio types grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-product-shop>
              {location.availableStudios.map((studio) => {
                const matchedService = services.find((s) =>
                  s.title.toLowerCase().includes(studio.type.toLowerCase().split(' ')[0])
                );
                return (
                  <div
                    key={studio.type}
                    className="bg-white rounded-2xl overflow-hidden border border-stone-100 hover:border-amber-200 transition-all duration-300"
                  >
                    <div className="relative h-32 overflow-hidden">
                      <img
                        src={
                          matchedService?.image ||
                          `https://public.readdy.ai/ai/img_res/b8415cced1ff8ed1471095bb4794ee08.jpg$%7Bstudio.type%7D%20studio%20interior%20premium%20modern%20environment&width=300&height=200&seq=loc-all-${location.slug}-${studio.type.replace(/\s/g, '-')}&orientation=landscape`
                        }
                        alt={studio.type}
                        className="w-full h-full object-cover object-top"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-stone-900/50 to-transparent" />
                      <div className="absolute top-3 left-3 w-8 h-8 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-lg">
                        <i className={`${studio.icon} text-amber-500 text-sm`} />
                      </div>
                    </div>
                    <div className="p-4">
                      <p className="font-semibold text-stone-900 text-xs mb-1 leading-snug">{studio.type}</p>
                      <p className="text-stone-500 text-[11px] leading-relaxed mb-2">{studio.desc}</p>
                      <span className="inline-block bg-amber-50 text-amber-700 text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap">
                        {studio.count} {studio.count === 1 ? 'Studio' : 'Studios'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Map */}
            <div className="mt-10 rounded-2xl overflow-hidden border border-stone-200 h-72">
              <iframe
                src={location.mapEmbed}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={`Map of studios in ${location.city}`}
              />
            </div>
          </div>
        </section>
      ))}
    </div>
  );
}
